import { Component, findComponents, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport } from '@gondel/plugin-media-queries';
import { WINDOW_RESIZED } from '@swisslife/shared-ui/src/utils/gondel-resize/js/gondel-resize';
import { browserDeviceTests } from '@swisslife/shared-ui/src/utils/browser-detection/js/browser-detection';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';
import { Image } from '@swisslife/patterns-base/src/atoms/image/js/image';
import '@swisslife/patterns-base/src/atoms/image/js/image';
import { ISlickOptions } from '@swisslife/shared-types/src/types/slick-carousel';

enum Selectors {
	Slider = '.js-m-image-slider__slider',
	SliderItem = '.js-m-image-slider__slider-item',
	SliderItemWrapper = '.js-m-image-slider__slider-item-wrapper',
	SliderItemContainer = '.js-m-image-slider__slider-item-container',
	SliderItemAnimator = '.js-m-image-slider__slider-item-animator',
	SliderItemContent = '.js-m-image-slider__slider-item-content',
	SliderItemLink = '.js-m-image-slider__slider-item-link',
	SliderItemDescription = '.js-m-image-slider__slider-item-description',
	SliderPrevButton = '.js-m-image-slider__slider-buttons-prev',
	SliderNextButton = '.js-m-image-slider__slider-buttons-next',
}

enum States {
	SliderActive = 'state-m-image-slider--active',
	SlideActiveAnimation = 'state-m-image-slider--active-animation',
	SlideActiveDescription = 'state-m-image-slider--active-description',
	SlideActiveLink = 'state-m-image-slider--active-link',
	SlideOpaque = 'state-m-image-slider__slider-item--opaque',
}

enum SlickStates {
	CurrentSlide = '.slick-current',
}

enum SliderType {
	Big = 'BIG',
	Small = 'SMALL',
}

@Component('ImageSlider')
export class ImageSlider extends GondelJqueryComponent {
	private controllers: any[] = Array();
	private $slider: any;
	private sliderInitialized: boolean = false;
	private slideBound: any = undefined;
	private slideInnerBound: any = undefined;
	private activeSlides: any[] = [];
	private activeSlideIndex: number;
	private activeSlideDebounceTimer: any;
	private sliderType: SliderType;
	private minimumNumberOfSlidesBig: number;
	private minimumNumberOfSlidesSmall: number;

	public start() {
		this.minimumNumberOfSlidesBig = 5;
		this.minimumNumberOfSlidesSmall = 4;
		this.$slider = this.$ctx.find(Selectors.Slider);
		this.sliderType = this.getSliderType();
		this.initImageSlider();
	}

	public async sync() {
		this.controllers = await addComponentAnimation(this.$ctx);
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}
		if (this.sliderInitialized) {
			this.deactivateAllSlides();
			this.resetSlickSlides();
			this.$slider.slick('unslick');
		}
	}

	@EventListener(WINDOW_RESIZED)
	public _handleWindowResizeEvent() {
		this.deactivateAllSlides();
		this.resetSlickSlides();
		this.slideBound = undefined;
		this.slideInnerBound = undefined;
		this.activeSlideIndex = -1;
		setTimeout(() => {
			// we need to wait a bit so that we
			// get the correct viewport size
			this.sliderType = this.getSliderType();
			this.refreshSlider();
		}, 100);
	}

	private async initImageSlider() {
		if (this.$slider) {
			const $slides = this.$slider.find(Selectors.SliderItem);
			// set special class clientside if slide has test (description)
			$slides.each((index, slide) => {
				const $slide = $(slide);
				const $description = $slide.find(Selectors.SliderItemDescription);

				if ($description.length) {
					$slide.addClass(States.SlideOpaque);
				}
			});
			// if number of slides < minimumNumberOfSlides, clone items until minimumNumberOfSlides is filled
			const numberOfInitialSlides = $slides.length;
			const minItems = this.returnMinimumNumberOfSlides();

			if (numberOfInitialSlides < minItems) {
				let numberOfClonesRequired = minItems - numberOfInitialSlides;

				while (numberOfClonesRequired > 0) {
					$slides.each((index, slide) => {
						const $slide = $(slide);

						$slide.clone().appendTo(this.$slider);
					});
					numberOfClonesRequired = minItems - this.$slider.find(Selectors.SliderItem).length;
				}
			}

			// tslint:disable-next-line
			await import(/* webpackChunkName: "dynamic-imports/slider-lib" */ '@swisslife/shared-ui/src/utils/slider/js/slider-lib');

			const slickOptions: ISlickOptions = {
				slidesToShow: 4,
				slidesToScroll: 1,
				centerMode: true,
				centerPadding: '0',
				adaptiveHeight: true,
				focusOnSelect: true,
				infinite: true,
				rows: 0, // https://github.com/kenwheeler/slick/issues/3110
				prevArrow: this.$ctx.find(Selectors.SliderPrevButton),
				nextArrow: this.$ctx.find(Selectors.SliderNextButton),
				responsive: [
					{
						breakpoint: 993,
						settings: {
							slidesToShow: 3,
							swipeToSlide: true,
							touchThreshold: 10,
						},
					},
				],
			};

			if (browserDeviceTests.ie) {
				slickOptions.useTransform = false;
			}

			this.$slider = (this.$slider as any).slick(slickOptions);

			this.$slider.on('beforeChange', (event: any, slick: any, currentSlide: any, nextSlide: any) => {
				if (nextSlide !== this.activeSlideIndex) {
					this.deactivateAllSlides();
					this.debouncedActivateSlide();
					this.activeSlideIndex = nextSlide;
				}
			});

			this.sliderInitialized = true;
			this.waitForImagesToLoad();
		}
	}

	private waitForImagesToLoad() {
		const imageComponents = findComponents<Image>(this._ctx, 'Image');
		if (imageComponents && imageComponents.length) {
			Promise.all(imageComponents.map((imageComponent) => imageComponent.getImageLoadedPromise)).then(() => {
				this.refreshSlider();
			});
		}
	}

	private refreshSlider() {
		// only act if the slider has actually been initialized
		if (this.sliderInitialized) {
			this.resetSlickSlides();
			// refresh slick when an image has been loaded to update the cloned slides and prevent image jumping
			this.$ctx.addClass(States.SliderActive);
			this.$slider.slick('refresh');
			setTimeout(() => {
				// we initialize the slides on the next tick
				// this is necessary on firefox
				if (this.sliderType === SliderType.Big) {
					this.initBigSlickSlider();
				}
				this.debouncedActivateSlide();
			}, 100);
		}
	}

	private returnMinimumNumberOfSlides() {
		if (this.sliderType === SliderType.Big) {
			return this.minimumNumberOfSlidesBig;
		} else {
			return this.minimumNumberOfSlidesSmall;
		}
	}

	private initBigSlickSlider() {
		const $slides = this.$ctx.find(Selectors.SliderItem);
		if ($slides.length) {
			$slides.each((index, slide) => {
				const $slide = $(slide);
				if ($slide) {
					this.initBigSlickSlide($slide);
				}
			});
		}
	}

	private initBigSlickSlide($slide: any) {
		const $wrapper = $slide.find(Selectors.SliderItemWrapper);
		if ($wrapper) {
			const bound = this.slideBound || {
				height: $wrapper.height(),
				width: $wrapper.width(),
			};
			if (!this.slideBound) {
				this.slideBound = bound;
			}
			const $container = $wrapper.find(Selectors.SliderItemContainer);
			if ($container) {
				const innerBound = this.slideInnerBound || {
					height: $container.height(),
					width: $container.width(),
				};
				if (!this.slideInnerBound) {
					this.slideInnerBound = innerBound;
				}
				const $animation = $container.find(Selectors.SliderItemAnimator);
				if ($animation) {
					const $content = $animation.find(Selectors.SliderItemContent);
					if ($content) {
						$wrapper.css({
							height: `${bound.height}px`,
							width: `${bound.width}px`,
						});
						$container.css({
							height: `${bound.height}px`,
							width: `${bound.width}px`,
						});
						$animation.css({
							position: 'absolute',
							top: '0',
							left: '0',
							height: `${bound.height}px`,
							width: `${bound.width + 32}px`,
						});
						$content.css({
							left: `-${(innerBound.width - bound.width - 64) / 2}px`,
							height: `${innerBound.height}px`,
							width: `${innerBound.width - 64}px`,
						});
						const $description = $content.find(Selectors.SliderItemDescription);
						if ($description) {
							$description.css({
								width: `${((innerBound.width - 64) * 2) / 3}px`,
							});
						}
					}
				}
			}
		}
	}

	private resetSlickSlides() {
		const $slides = this.$ctx.find(Selectors.SliderItem);
		if ($slides.length) {
			$slides.each((index, slide) => {
				const $slide = $(slide);
				if ($slide) {
					this.resetSlickSlide($slide);
				}
			});
		}
	}

	private resetSlickSlide($slide: any) {
		const $wrapper = $slide.find(Selectors.SliderItemWrapper);
		if ($wrapper) {
			const $container = $wrapper.find(Selectors.SliderItemContainer);
			if ($container) {
				const $animation = $container.find(Selectors.SliderItemAnimator);
				if ($animation) {
					const $content = $animation.find(Selectors.SliderItemContent);
					if ($content) {
						$wrapper.css({
							height: '',
							width: '',
						});
						$container.css({
							height: '',
							width: '',
						});
						$animation.css({
							position: '',
							top: '',
							left: '',
							transform: '',
							height: '',
							width: '',
						});
						$content.css({
							left: '',
							transform: '',
							height: '',
							width: '',
						});
						const $description = $content.find(Selectors.SliderItemDescription);
						if ($description) {
							$description.css({
								width: '',
							});
						}
					}
				}
			}
		}
	}

	private getActiveSlide() {
		return this.$ctx.find(SlickStates.CurrentSlide);
	}

	private deactivateAllSlides() {
		while (this.activeSlides.length) {
			const $slide = this.activeSlides.shift();
			this.deactivateSlide($slide);
		}
	}

	private deactivateSlide($slide: any) {
		if ($slide) {
			$slide.removeClass(States.SlideActiveLink);
			this.animateSlideOut($slide);
		}
	}

	private activateSlide($slide: any) {
		if ($slide) {
			this.activeSlides.push($slide);
			this.animateSlideIn($slide);
			$slide.addClass(States.SlideActiveLink);
		}
	}

	private animateSlideIn($slide: any) {
		if (this.getSliderType() === SliderType.Big && this.slideInnerBound) {
			const $animation = $slide.find(Selectors.SliderItemAnimator);
			const $content = $slide.find(Selectors.SliderItemContent);
			if ($animation) {
				$slide.addClass(`${States.SlideActiveAnimation} ${States.SlideActiveDescription}`);
				$animation.css({
					transform: `translateX(-${(this.slideInnerBound.width - this.slideBound.width - 64) / 2}px)`,
					width: `${this.slideInnerBound.width}px`,
				});
				$content.css({
					transform: `translateX(${(this.slideInnerBound.width - this.slideBound.width - 64) / 2}px)`,
				});
			}
		}
	}

	private animateSlideOut($slide: any) {
		if (this.getSliderType() === SliderType.Big && this.slideBound) {
			const $animation = $slide.find(Selectors.SliderItemAnimator);
			const $content = $slide.find(Selectors.SliderItemContent);
			if ($animation && $content) {
				$animation.css({
					transform: 'translateX(0)',
					width: `${this.slideBound.width + 32}px`,
				});
				$content.css({
					transform: 'translate(0)',
				});
				$slide.removeClass(States.SlideActiveDescription);
				setTimeout(() => {
					$slide.removeClass(States.SlideActiveAnimation);
				}, 250);
			}
		}
	}

	private debouncedActivateSlideLaterFn = () => {
		this.activeSlideDebounceTimer = null;
		const $slide = this.getActiveSlide();
		if ($slide) {
			this.activateSlide($slide);
		}
	};

	private debouncedActivateSlide() {
		clearTimeout(this.activeSlideDebounceTimer);
		this.activeSlideDebounceTimer = setTimeout(this.debouncedActivateSlideLaterFn, 300);
	}

	private getSliderType() {
		return ['medium', 'large', 'xlarge', 'xxlarge'].indexOf(getCurrentViewport()) >= 0
			? SliderType.Big
			: SliderType.Small;
	}
}
